import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const INDUSTRIES = [
  { name: "Real Estate", tag: "Sell units before they're built", items: ["Photoreal 3D walkthroughs & CGI launch films", "High-converting project microsites with booking", "Performance ads that reach qualified buyers", "Brand identity & premium sales collateral"] },
  { name: "Luxury Hotels & Resorts", tag: "Turn views into direct bookings", items: ["Cinematic property films & drone shoots", "Direct-booking website with rich storytelling", "Meta & Google campaigns for high-value stays", "Champagne-gold brand system"] },
  { name: "Hospitals & Doctors", tag: "Trust that converts to appointments", items: ["Calm, accessible booking website & app", "Doctor & facility video introductions", "Local performance marketing & SEO", "Reputation-building brand design"] },
  { name: "Elite Coaches", tag: "Scale a magnetic personal brand", items: ["High-ticket funnel & landing pages", "Short-form content & ad creatives", "Automation for lead nurture", "Signature personal-brand identity"] },
  { name: "Astrologers", tag: "Premium, credible digital presence", items: ["Consultation booking site with payments", "Cinematic intro & testimonial reels", "Targeted awareness campaigns", "Mystic-yet-modern branding"] },
  { name: "Interior Designers", tag: "Showcase craft, attract UHNW clients", items: ["Editorial portfolio website", "3D room visualisations & walkthroughs", "Brand film & tactile photography", "Instagram-first content system"] },
  { name: "Carpet & Furniture", tag: "Render 200+ SKUs without endless shoots", items: ["CGI product pipeline in room settings", "Interactive 3D product configurator", "E-commerce / catalogue website", "Performance ads for D2C sales"] },
  { name: "Home Furnishing", tag: "Make every product look premium", items: ["Studio & lifestyle product photography", "CGI ads & motion graphics", "Shoppable website & campaigns", "Cohesive brand identity"] },
  { name: "Clothing & Apparel", tag: "Build a scroll-stopping D2C brand", items: ["Lookbook films & product photography", "Shopify/custom storefront", "Paid social growth engine", "Fashion-forward branding"] },
  { name: "D2C / B2C / B2B Brands", tag: "Full-stack growth under one roof", items: ["Website, app & custom software", "Cinematic + CGI ad production", "Performance marketing & automation", "AI agents for support & ops"] },
];

export const Industries = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="industries" className="relative mx-auto max-w-[1400px] px-6 sm:px-10 py-24 lg:py-36">
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
        <Reveal>
          <SectionLabel>Industries we serve</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]">
            Built for <span className="text-gradient">high-ticket</span> categories.
          </h2>
          <p className="mt-6 font-body text-[var(--text-secondary)] max-w-md leading-relaxed">
            Tap any niche to see exactly how we help — tailored to what that category needs to win.
          </p>
        </Reveal>

        <div className="flex flex-wrap gap-3">
          {INDUSTRIES.map((ind, i) => (
            <motion.button
              key={ind.name}
              data-testid={`industry-${i}`}
              onClick={() => setActive(ind)}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              whileHover={{ y: -4 }}
              className="group inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] px-5 py-3 font-heading text-sm sm:text-base text-[var(--text-secondary)] transition-colors hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]"
            >
              {ind.name}
              <ArrowUpRight size={15} className="text-[var(--text-muted)] transition-all group-hover:text-[var(--accent-primary)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <NicheModal ind={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};

const NicheModal = ({ ind, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    onClick={onClose}
    className="fixed inset-0 z-[95] grid place-items-center bg-black/80 backdrop-blur-md p-4"
    data-testid="niche-modal"
  >
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.97 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-secondary)]"
    >
      {/* cinematic animated backdrop — "movie running" */}
      <div className="relative h-40 sm:h-48 overflow-hidden">
        <motion.div
          className="absolute inset-0"
          style={{ background: "var(--accent-gradient)", opacity: 0.5 }}
          animate={{ backgroundPositionX: ["0%", "100%"], scale: [1.1, 1.25, 1.1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute inset-y-0 w-1/3"
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)" }}
          animate={{ x: ["-120%", "420%"] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-secondary)] to-transparent" />
        <div className="absolute bottom-5 left-6 right-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--accent-foreground)]/90">How we help</span>
          <h3 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight">{ind.name}</h3>
        </div>
        <button onClick={onClose} data-testid="niche-modal-back"
          className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full glass px-4 py-2 font-heading text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--border-hover)]">
          <ArrowLeft size={14} /> Back
        </button>
        <button onClick={onClose} data-testid="niche-modal-close" aria-label="Close"
          className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full glass hover:border-[var(--border-hover)]">
          <X size={16} />
        </button>
      </div>

      <div className="p-6 sm:p-8">
        <p className="font-heading text-lg font-semibold text-gradient mb-6">{ind.tag}</p>
        <div className="space-y-3">
          {ind.items.map((it, i) => (
            <motion.div key={it}
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.1 }}
              className="flex items-start gap-3 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4">
              <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full btn-accent"><Check size={13} /></span>
              <span className="font-body text-[var(--text-primary)]">{it}</span>
            </motion.div>
          ))}
        </div>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a href="#contact" onClick={onClose} className="btn-accent inline-flex rounded-full px-7 py-3.5 font-heading text-sm font-semibold">
            Start your {ind.name} project
          </a>
          <button onClick={onClose} data-testid="niche-modal-back-bottom"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] px-6 py-3.5 font-heading text-sm font-medium text-[var(--text-secondary)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)] transition-colors">
            <ArrowLeft size={15} /> Back to industries
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);
